import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, Link } from "react-router-dom";
import { Loader2, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Sidebar } from "./Sidebar";
import { cn } from "@/lib/utils";
import { GlobalSearch } from "@/components/core/GlobalSearch";
import { MobileBottomNav } from "@/components/core/MobileNav";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useDesktop } from "@/hooks/useMedia";

export const FullLoader = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="app-loader">
    <Loader2 className="h-8 w-8 animate-spin text-[#0A1128]" />
  </div>
);

export const ProtectedLayout = () => {
  const { user } = useAuth();
  const desktop = useDesktop();
  const { pathname } = useLocation();
  const [drawer, setDrawer] = useState(false);
  useEffect(() => setDrawer(false), [pathname, desktop]);
  if (user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen">
      {desktop ? <Sidebar /> : (
        <Sheet open={drawer} onOpenChange={setDrawer}>
          <SheetContent side="left" className="p-0 w-[280px] max-w-[85vw] border-0 bg-[#6BBAF0] [&>button]:hidden" data-testid="mobile-drawer" aria-describedby={undefined}>
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <Sidebar drawer />
          </SheetContent>
        </Sheet>
      )}
      <main className="lg:pl-[260px] min-w-0">
        <div className="sticky top-0 z-30 bg-[#7CC6FA]/85 backdrop-blur-md border-b border-[#5FB3EA]/60 pt-[env(safe-area-inset-top)]" data-testid="top-header">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-10 py-2.5 lg:py-3 flex items-center justify-between lg:justify-end gap-3">
            {!desktop && (
              <div className="flex items-center gap-2">
                <button onClick={() => setDrawer(true)} className="h-11 w-11 rounded-full bg-[#A6DAFC] border border-[#5FB3EA] flex items-center justify-center text-[#0A1128]" aria-label="Apri menu" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
                <Link to="/dashboard" className="flex items-center" data-testid="mobile-header-logo"><img src="/brand/logo-core.png" alt="Core" className="h-7 w-auto" /></Link>
              </div>
            )}
            <GlobalSearch />
          </div>
        </div>
        <div className="core-main max-w-[1400px] mx-auto px-4 md:px-8 lg:px-10 pt-5 lg:py-9 pb-28 md:pb-10"><Outlet /></div>
      </main>
      <MobileBottomNav onMenu={() => setDrawer(true)} />
    </div>
  );
};

export const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
};

export const PageHeader = ({ eyebrow, title, subtitle, actions }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 sm:gap-6 mb-6 lg:mb-8 core-rise">
    <div className="min-w-0">
      {eyebrow && <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#E8173A] mb-2">{eyebrow}</div>}
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-[#0A1128] break-words" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-2">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2 sm:gap-3">{actions}</div>}
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, action, testid = "empty-state" }) => (
  <div className="flex flex-col items-center justify-center text-center py-14 px-6" data-testid={testid}>
    {Icon && (
      <div className="h-14 w-14 rounded-2xl bg-[#A6DAFC] flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-[#0A1128]" />
      </div>
    )}
    <div className="font-display font-extrabold text-lg text-[#0A1128]">{title}</div>
    {text && <p className="text-sm text-slate-500 mt-1 max-w-sm">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Panel = ({ children, className = "", ...rest }) => (
  <div className={cn("bg-[#8FD0FB] rounded-2xl border border-[#5FB3EA] shadow-[0_2px_10px_rgba(10,17,40,0.08)]", className)} {...rest}>
    {children}
  </div>
);
